import { ProgressCircle } from "antd-mobile"
import styled from "styled-components"
import { formatDisplayNumber, roundByDecimalPlace } from "utils"
import { DailyProgressBox, ProgressBox, ProgressCaloriesTitle } from "../styled"
import CircleIcon from "./CircleIcon"
import { useAppSelector } from "app/hooks"
import FireIcon from "components/Icons/FireIcon"
import RemainingNutrition from "./IntakeNutrition"
import { FlexBox } from "components/Common/FlexBox"
import CountUp from "react-countup"
import { IDailyProgress } from "features/home/homeSlice.types"
import { useDiaryAnimationLoading } from "features/home/hooks"
import { useEffect, useState } from "react"

const KcalNumber = styled.p<{ $isOver: boolean; $isShow: boolean }>`
  position: absolute;
  font-size: 32px;
  font-weight: 800;
  color: ${({ $isOver }) => ($isOver ? "#FB6D6D" : "#36EDFE")};
  text-align: center;
  height: 39px;
  line-height: 120%;
  letter-spacing: 1.28px;
  opacity: 0;
  transition: all 0.2s linear;
  ${(props) => props.$isShow && "opacity:1"}
`

const KcalDescription = styled.p`
  color: var(--Primitive-White, #fff);
  text-align: center;
  font-size: 14px;
  line-height: 17px;
  height: 17px;
  letter-spacing: 0.12px;
  font-weight: 500;
  margin-top: -6px;
`

const getDisplayPercent = (isOver: boolean, dailyProgress?: IDailyProgress) => {
  if (!dailyProgress?.goal) return 0

  const {
    calorie: gainedCalorie,
    goal: { caloriePerDay: goalCalories },
  } = dailyProgress

  let result = 0

  if (isOver) {
    result = 100
  } else {
    result = (gainedCalorie / goalCalories) * 100
  }

  if (result > 100) {
    result = 100
  }

  return roundByDecimalPlace(result, 0)
}

const RemainingCircleProgress = () => {
  const dailyProgress = useAppSelector((state) => state.home.dailyProgress)
  const gainedCalorie = dailyProgress?.calorie ?? 0
  const goalCalories = dailyProgress?.goal?.caloriePerDay ?? 0
  const loading = useAppSelector((state) => state.home.getDailyProgressLoading)
  const getDailyProgressCounter = useAppSelector(
    (state) => state.home.getDailyProgressCounter,
  )
  const localLoading = useDiaryAnimationLoading(getDailyProgressCounter)
  const animationLoading = localLoading || loading

  const isOver = dailyProgress?.goal
    ? dailyProgress.calorie > dailyProgress.goal.caloriePerDay
    : false

  const fillColor = isOver ? "#FB6D6D" : "var(--color-primary)"

  const [persistentGoalCalories, setPersistentGoalCalories] =
    useState(goalCalories)

  useEffect(() => {
    if (goalCalories !== 0) {
      setPersistentGoalCalories(goalCalories)
    }
  }, [goalCalories])

  return (
    <ProgressBox>
      <CircleIcon />

      <ProgressCircle
        percent={
          animationLoading ? 0 : getDisplayPercent(isOver, dailyProgress)
        }
        style={{
          "--track-width": "7px",
          "--size": "140px",
          "--fill-color": fillColor,
          "--track-color": "transparent",
          zIndex: 2,
          position: "relative",
        }}
      >
        <FlexBox
          style={{ position: "relative", top: -2 }}
          $direction="column"
          $gap={2}
          $alignItems="center"
          $justify="center"
        >
          <FlexBox $direction="column" $alignItems="center" $justify="center">
            <FireIcon />
            <ProgressCaloriesTitle>Calories</ProgressCaloriesTitle>
          </FlexBox>

          <div>
            <FlexBox
              style={{ height: 39, position: "relative", minHeight: 39 }}
              $direction="column"
              $alignItems="center"
              $justify="center"
            >
              <KcalNumber $isOver={isOver} $isShow={animationLoading}>
                -
              </KcalNumber>
              <KcalNumber $isOver={isOver} $isShow={!animationLoading}>
                <CountUp
                  separator={","}
                  style={{ display: "inline-block", lineHeight: "39px" }}
                  end={roundByDecimalPlace(gainedCalorie, 0)}
                  duration={0.5}
                ></CountUp>
              </KcalNumber>
            </FlexBox>

            <KcalDescription>
              /{formatDisplayNumber(persistentGoalCalories, 0)}
            </KcalDescription>
          </div>
        </FlexBox>
      </ProgressCircle>
    </ProgressBox>
  )
}

const IntakeDailyProgress = () => {
  return (
    <DailyProgressBox>
      <RemainingNutrition />
      <RemainingCircleProgress />
    </DailyProgressBox>
  )
}

export default IntakeDailyProgress
